import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { X, Search, Send, Paperclip, ArrowLeft, MoreVertical, LogOut, Check, CheckCheck, MessageSquare, Phone, Download, FileText, ExternalLink, Smile, CornerUpRight, CornerUpLeft, Users, Pin, PinOff, ChevronUp, ChevronDown, Calendar, Palette } from "lucide-react";
import axios from "axios";
import EmojiPicker from 'emoji-picker-react';
import socket from "../../socket";
import {
  setChatDrawerOpen,
  setActiveConversation,
  fetchMessages,
  createOrGetConversation,
  resetChatUnreadCount,
  manageGroupChat
} from "../../Redux/Slice/ChatSlice";
import defaultProfile from "../../Image/Daynamic_Logo_Piture.png";
import mainLogo from "../../Image/Main_logo.png";
import "./ChatDrawer.css";

const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

const themes = {
  default: {
    name: "Classic Blue",
    primary: "#1e3a8a",
    accent: "#3b82f6",
    bubble: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
    viewportBg: "#f8fafc",
    receiverBg: "#ffffff",
    textColor: "#0f172a",
    receiverTextColor: "#0f172a",
    botBg: "linear-gradient(135deg, #f8fafc 0%, #f0f7ff 100%)",
    botBorder: "1px solid rgba(226, 232, 240, 0.8)"
  },
  lavender: {
    name: "Lavender",
    primary: "#6366f1",
    accent: "#a855f7",
    bubble: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
    viewportBg: "#faf5ff",
    receiverBg: "#fbfaff",
    textColor: "#0f172a",
    receiverTextColor: "#0f172a",
    botBg: "linear-gradient(135deg, #fdfcff 0%, #f5f3ff 100%)",
    botBorder: "1px solid rgba(237, 233, 254, 0.8)"
  },
  sunset: {
    name: "Sunset Glow",
    primary: "#f43f5e",
    accent: "#fb923c",
    bubble: "linear-gradient(135deg, #f43f5e 0%, #fb923c 100%)",
    viewportBg: "#fff7ed",
    receiverBg: "#fffaf9",
    textColor: "#0f172a",
    receiverTextColor: "#0f172a",
    botBg: "linear-gradient(135deg, #fffafb 0%, #fff1f2 100%)",
    botBorder: "1px solid rgba(254, 226, 226, 0.8)"
  },
  emerald: {
    name: "Emerald",
    primary: "#059669",
    accent: "#10b981",
    bubble: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
    viewportBg: "#f0fdf4",
    receiverBg: "#fafdfb",
    textColor: "#0f172a",
    receiverTextColor: "#0f172a",
    botBg: "linear-gradient(135deg, #f7fee7 0%, #ecfdf5 100%)",
    botBorder: "1px solid rgba(209, 250, 229, 0.8)"
  },
  mint: {
    name: "Mint Teal",
    primary: "#0d9488",
    accent: "#14b8a6",
    bubble: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)",
    viewportBg: "#f0fdfa",
    receiverBg: "#ffffff",
    textColor: "#0f172a",
    receiverTextColor: "#0f172a",
    botBg: "linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)",
    botBorder: "1px solid rgba(20, 184, 166, 0.8)"
  },
  cherry: {
    name: "Sakura Pink",
    primary: "#db2777",
    accent: "#f472b6",
    bubble: "linear-gradient(135deg, #db2777 0%, #f472b6 100%)",
    viewportBg: "#fff1f2",
    receiverBg: "#ffffff",
    textColor: "#0f172a",
    receiverTextColor: "#0f172a",
    botBg: "linear-gradient(135deg, #fff5f6 0%, #ffe4e6 100%)",
    botBorder: "1px solid rgba(244, 114, 182, 0.8)"
  },
  honey: {
    name: "Amber Gold",
    primary: "#d97706",
    accent: "#f59e0b",
    bubble: "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)",
    viewportBg: "#fef8e6",
    receiverBg: "#ffffff",
    textColor: "#0f172a",
    receiverTextColor: "#0f172a",
    botBg: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
    botBorder: "1px solid rgba(245, 158, 11, 0.8)"
  },
  royal: {
    name: "Plum Purple",
    primary: "#6b21a8",
    accent: "#a855f7",
    bubble: "linear-gradient(135deg, #6b21a8 0%, #a855f7 100%)",
    viewportBg: "#faf5ff",
    receiverBg: "#ffffff",
    textColor: "#0f172a",
    receiverTextColor: "#0f172a",
    botBg: "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)",
    botBorder: "1px solid rgba(168, 85, 247, 0.8)"
  },
  slate: {
    name: "Steel Slate",
    primary: "#475569",
    accent: "#64748b",
    bubble: "linear-gradient(135deg, #475569 0%, #64748b 100%)",
    viewportBg: "#f8fafc",
    receiverBg: "#ffffff",
    textColor: "#0f172a",
    receiverTextColor: "#0f172a",
    botBg: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    botBorder: "1px solid rgba(100, 116, 139, 0.8)"
  },
  ocean: {
    name: "Ocean Dark",
    primary: "#0f172a",
    accent: "#0d9488",
    bubble: "linear-gradient(135deg, #0f172a 0%, #0d9488 100%)",
    viewportBg: "#0b0f19",
    receiverBg: "#1e293b",
    textColor: "#f8fafc",
    receiverTextColor: "#f1f5f9",
    isDark: true,
    botBg: "linear-gradient(135deg, #111e2e 0%, #0b0f19 100%)",
    botBorder: "1px solid rgba(13, 148, 136, 0.3)"
  },
  cyberpunk: {
    name: "Cyberpunk",
    primary: "#ec4899",
    accent: "#06b6d4",
    bubble: "linear-gradient(135deg, #ec4899 0%, #06b6d4 100%)",
    viewportBg: "#0f172a",
    receiverBg: "#1e293b",
    textColor: "#f8fafc",
    receiverTextColor: "#f1f5f9",
    isDark: true,
    botBg: "linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)",
    botBorder: "1px solid rgba(99, 102, 241, 0.3)"
  },
  dark: {
    name: "Carbon Dark",
    primary: "#374151",
    accent: "#4b5563",
    bubble: "linear-gradient(135deg, #374151 0%, #4b5563 100%)",
    viewportBg: "#111827",
    receiverBg: "#1f2937",
    textColor: "#f9fafb",
    receiverTextColor: "#f3f4f6",
    isDark: true,
    botBg: "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
    botBorder: "1px solid rgba(75, 85, 99, 0.3)"
  }
};

const ChatDrawer = () => {
  const dispatch = useDispatch();
  const {
    isChatDrawerOpen,
    conversations,
    activeConversation,
    messages,
    members,
    onlineUsers,
    typingUsers,
    chatEnabled,
    sendEnabled,
    messagesLoading,
    hasMoreMessages,
    messagesPage
  } = useSelector((state) => state.chat);

  const authData = useSelector((state) => state.auth.LoginData || {});
  const currentUserId = authData?.data?._id;

  const myMemberInfo = useMemo(() => {
    if (!members || !currentUserId) return null;
    return members.find((m) => m._id?.toString() === currentUserId?.toString());
  }, [members, currentUserId]);

  const [activeTab, setActiveTab] = useState("chats"); // 'chats' or 'contacts'
  const [searchQuery, setSearchQuery] = useState("");
  const [inputText, setInputText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showGroupOptions, setShowGroupOptions] = useState(false);
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(() => {
    try {
      return localStorage.getItem("chat_theme") || "default";
    } catch (e) {
      return "default";
    }
  });
  const [isUploading, setIsUploading] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [fileSizeLimit, setFileSizeLimit] = useState(25); // default 25MB
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [activeReactionMessageId, setActiveReactionMessageId] = useState(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [messageToForward, setMessageToForward] = useState(null);
  const [forwardSearchQuery, setForwardSearchQuery] = useState("");
  const [selectedForwardChats, setSelectedForwardChats] = useState([]); // Array of conversation IDs
  const [replyingToMessage, setReplyingToMessage] = useState(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  // Pasted and upload file/image states for WhatsApp-style confirmation
  const [pendingSendFile, setPendingSendFile] = useState(null);
  const [pendingSendFiles, setPendingSendFiles] = useState([]);
  const [pendingTotalFileSize, setPendingTotalFileSize] = useState(0);
  const [pendingCaption, setPendingCaption] = useState("");
  const [filePreviewUrl, setFilePreviewUrl] = useState("");
  const [isConfirmingSend, setIsConfirmingSend] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [expandedMessageIds, setExpandedMessageIds] = useState({});

  const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes) || bytes <= 0) return "";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  // WhatsApp style Pinning & In-Chat Search state
  const [pinnedConvIds, setPinnedConvIds] = useState(() => {
    try {
      const saved = localStorage.getItem("pinned_chat_ids");
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  const [selectedMonthOffset, setSelectedMonthOffset] = useState(0);

  const getMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const label = i === 0 ? "Current Month" : i === 1 ? "Previous Month" : "2 Months Ago";
      const monthName = d.toLocaleString("default", { month: "long", year: "numeric" });
      months.push({ offset: i, label: `${monthName} (${label})`, name: monthName });
    }
    return months;
  };
  const monthOptions = getMonthOptions();

  const [showInChatSearch, setShowInChatSearch] = useState(false);
  const [inChatSearchQuery, setInChatSearchQuery] = useState("");
  const [currentSearchMatchIndex, setCurrentSearchMatchIndex] = useState(0);

  // Get recipient info in one-to-one chat
  const getRecipientInfo = (conv) => {
    if (!conv || !conv.participants) return null;
    return conv.participants.find((p) => (p._id || p) !== currentUserId);
  };

  const isDynamicManagementConv = (conv) => {
    if (!conv) return false;
    const cName = (conv.name || "").trim().toLowerCase();
    if (cName.includes("dynamic management")) return true;

    const recipient = getRecipientInfo(conv);
    if (recipient) {
      const rName = (recipient.Name || "").trim().toLowerCase();
      const rEmail = (recipient.Email || "").trim().toLowerCase();
      const rRole = (recipient.Role || "").trim().toLowerCase();

      if (
        rName.includes("dynamic management") ||
        rEmail.includes("management@dynamic.com") ||
        rRole.includes("dynamic management")
      ) {
        return true;
      }
    }
    return false;
  };

  const isDynamicManagement = activeConversation && isDynamicManagementConv(activeConversation);

  const togglePinConv = (convId, e) => {
    if (e) e.stopPropagation();
    setPinnedConvIds((prev) => {
      const isPinned = prev.includes(convId);
      const updated = isPinned ? prev.filter((id) => id !== convId) : [...prev, convId];
      try {
        localStorage.setItem("pinned_chat_ids", JSON.stringify(updated));
      } catch (err) { }
      return updated;
    });
  };

  // Find matching messages for in-chat search
  const matchingMessages = useMemo(() => {
    if (!inChatSearchQuery.trim() || !messages) return [];
    const q = inChatSearchQuery.trim().toLowerCase();
    return messages.filter((m) => {
      if (!m) return false;
      const type = m.messageType || "text";
      if (type === "text" || type === "announcement") {
        return m.message && typeof m.message === "string" && m.message.toLowerCase().includes(q);
      } else if (type === "image" || type === "file") {
        return m.caption && typeof m.caption === "string" && m.caption.toLowerCase().includes(q);
      }
      return false;
    });
  }, [inChatSearchQuery, messages]);

  useEffect(() => {
    setCurrentSearchMatchIndex(0);
    if (matchingMessages.length > 0) {
      scrollToMessage(matchingMessages[0]._id);
    }
  }, [inChatSearchQuery]);

  const handleNextMatch = () => {
    if (matchingMessages.length === 0) return;
    const nextIdx = (currentSearchMatchIndex + 1) % matchingMessages.length;
    setCurrentSearchMatchIndex(nextIdx);
    scrollToMessage(matchingMessages[nextIdx]._id);
  };

  const handlePrevMatch = () => {
    if (matchingMessages.length === 0) return;
    const prevIdx = (currentSearchMatchIndex - 1 + matchingMessages.length) % matchingMessages.length;
    setCurrentSearchMatchIndex(prevIdx);
    scrollToMessage(matchingMessages[prevIdx]._id);
  };

  const highlightText = (content) => {
    if (!content || typeof content !== "string") return content;
    if (!inChatSearchQuery || !inChatSearchQuery.trim()) return content;

    const query = inChatSearchQuery.trim();
    const parts = content.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));

    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={i} style={{ backgroundColor: "#fde047", color: "#000", padding: "0 2px", borderRadius: "2px", fontWeight: "bold" }}>
          {part}
        </mark>
      ) : part
    );
  };

  const shouldTruncateText = (text) => {
    if (!text) return false;
    const lines = text.split("\n").length;
    return text.length > 400 || lines > 8;
  };

  const getTruncatedText = (text) => {
    if (!text) return "";
    const lines = text.split("\n");
    if (lines.length > 8) {
      return lines.slice(0, 8).join("\n") + "...";
    }
    if (text.length > 400) {
      return text.substring(0, 400) + "...";
    }
    return text;
  };

  const parseMessageMarkdown = (content, isSender, isBot = false) => {
    if (!content || typeof content !== "string") return content;

    const parts = content.split("```");
    return parts.map((part, index) => {
      const isCodeBlock = index % 2 === 1;
      if (isCodeBlock) {
        let cleanPart = part;
        if (cleanPart.startsWith("text\n")) {
          cleanPart = cleanPart.substring(5);
        } else if (cleanPart.startsWith("\n")) {
          cleanPart = cleanPart.substring(1);
        }
        return (
          <pre
            key={index}
            style={{
              fontFamily: 'monospace, Courier New, Courier',
              fontSize: window.innerWidth < 640 ? '9px' : '11px',
              backgroundColor: isSender ? 'rgba(0, 0, 0, 0.15)' : isBot ? '#ffffff' : '#f1f5f9',
              color: isSender ? '#ffffff' : '#0f172a',
              padding: window.innerWidth < 640 ? '6px 8px' : (isBot ? '10px 12px' : '8px 10px'),
              borderRadius: '8px',
              margin: '8px 0',
              overflowX: 'hidden',
              whiteSpace: 'pre',
              border: isSender ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid #e2e8f0',
              lineHeight: isBot ? '1.55' : '1.4',
              boxShadow: isBot ? '0 1px 3px rgba(0,0,0,0.04)' : 'none'
            }}
          >
            {cleanPart}
          </pre>
        );
      } else {
        const lines = part.split("\n");
        return lines.map((line, lineIdx) => {
          const boldParts = line.split("*");
          const renderedLine = boldParts.map((bPart, bIdx) => {
            const isBold = bIdx % 2 === 1;
            if (isBold) {
              return <strong key={bIdx} style={{ fontWeight: '800' }}>{highlightText(bPart)}</strong>;
            }
            return <React.Fragment key={bIdx}>{highlightText(bPart)}</React.Fragment>;
          });

          return (
            <React.Fragment key={lineIdx}>
              {renderedLine}
              {lineIdx < lines.length - 1 && <br />}
            </React.Fragment>
          );
        });
      }
    });
  };

  const renderExpandableText = (text, msgId, isCaption = false, isSender = false) => {
    if (!text) return null;
    const isDM = isDynamicManagementConv(activeConversation);
    const isBot = !isSender && isDM;
    const needsTruncation = isDM ? false : shouldTruncateText(text);
    const isExpanded = expandedMessageIds[msgId];

    const displayLinkStyle = {
      color: isSender ? "#fff" : "#1e3a8a",
      fontWeight: 800,
      cursor: "pointer",
      marginLeft: "6px",
      fontSize: "12px",
      textDecoration: "underline",
      display: "inline-block"
    };

    const botTextStyle = isBot ? {
      fontSize: "13px",
      lineHeight: "1.6",
      color: "#1e293b",
      fontWeight: "500"
    } : {};

    if (needsTruncation && !isExpanded) {
      return (
        <div
          className={isCaption ? "saas-message-text font-medium animate__animated animate__fadeIn" : "saas-message-text animate__animated animate__fadeIn"}
          style={{
            ...(isCaption ? { margin: "8px 2px 2px 2px", wordBreak: "break-word" } : {}),
            ...botTextStyle
          }}
        >
          {parseMessageMarkdown(getTruncatedText(text), isSender, isBot)}
          <span
            onClick={(e) => {
              e.stopPropagation();
              setExpandedMessageIds(prev => ({ ...prev, [msgId]: true }));
            }}
            style={displayLinkStyle}
          >
            Read More
          </span>
        </div>
      );
    }

    return (
      <div
        className={isCaption ? "saas-message-text font-medium animate__animated animate__fadeIn" : "saas-message-text animate__animated animate__fadeIn"}
        style={{
          ...(isCaption ? { margin: "8px 2px 2px 2px", wordBreak: "break-word" } : {}),
          ...botTextStyle
        }}
      >
        {parseMessageMarkdown(text, isSender, isBot)}
        {needsTruncation && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              setExpandedMessageIds(prev => ({ ...prev, [msgId]: false }));
            }}
            style={displayLinkStyle}
          >
            Read Less
          </span>
        )}
      </div>
    );
  };

  const onEmojiClick = (emojiObject) => {
    setInputText(prev => prev + emojiObject.emoji);
  };

  const handleMessageReaction = (emojiObject) => {
    if (!activeReactionMessageId || !activeConversation) return;

    socket.emit("react_to_message", {
      messageId: activeReactionMessageId,
      conversationId: activeConversation._id,
      emoji: emojiObject.emoji
    });

    setActiveReactionMessageId(null);
    setHoveredMessageId(null);
  };

  const handleForwardMessage = () => {
    if (!messageToForward || selectedForwardChats.length === 0) return;

    selectedForwardChats.forEach((convId) => {
      const payload = {
        conversationId: convId,
        message: messageToForward.message,
        messageType: messageToForward.messageType,
        isForwarded: true
      };
      socket.emit("send_message", payload);
    });

    setShowForwardModal(false);
    setMessageToForward(null);
    setSelectedForwardChats([]);
  };

  const scrollToMessage = (messageId) => {
    const element = document.getElementById(`msg-${messageId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.classList.add("message-flash-highlight");
      setTimeout(() => {
        element.classList.remove("message-flash-highlight");
      }, 1200);
    }
  };

  const formatDateTimeForFilename = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const strHours = String(hours).padStart(2, "0");
    return `${day}-${month}-${year} ${strHours}-${minutes} ${ampm}`;
  };

  const getFileName = (url, createdAt) => {
    if (!url) return "File - Dynamic";
    const parts = url.split("/");
    const lastPart = parts[parts.length - 1];
    const underscoreIndex = lastPart.indexOf("_");
    const originalName = underscoreIndex !== -1
      ? decodeURIComponent(lastPart.substring(underscoreIndex + 1))
      : decodeURIComponent(lastPart);

    const dateSuffix = createdAt ? ` (${formatDateTimeForFilename(createdAt)})` : "";
    const suffix = ` - Dynamic Management Message${dateSuffix}`;

    const lastDotIndex = originalName.lastIndexOf(".");
    if (lastDotIndex !== -1) {
      const name = originalName.substring(0, lastDotIndex);
      const ext = originalName.substring(lastDotIndex);
      // Using '-' instead of '/' because '/' is not allowed in file names
      return `${name}${suffix}${ext}`;
    }
    return `${originalName}${suffix}`;
  };

  const getFileExtension = (url) => {
    if (!url) return "DOC";
    const parts = url.split(".");
    const ext = parts[parts.length - 1].toUpperCase();
    return ext.length <= 4 ? ext : "DOC";
  };

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const composerEmojiRef = useRef(null);
  const messageEmojiRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const prevActiveConvIdRef = useRef(null);
  const prevMessagesLengthRef = useRef(0);

  // Auto-scroll to bottom of messages
  const scrollToBottom = (instant = false) => {
    const container = messagesContainerRef.current;
    if (container) {
      if (instant) {
        container.scrollTop = container.scrollHeight;
      } else {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth"
        });
      }
    }
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: instant ? "auto" : "smooth",
        block: "end"
      });
    }
    setShowScrollToBottom(false);
  };

  // Multi-pass scroll for reliable bottom positioning across image loads and layout passes
  const executeMultiPassScroll = (instant = true) => {
    scrollToBottom(instant);
    requestAnimationFrame(() => scrollToBottom(instant));
    setTimeout(() => scrollToBottom(instant), 50);
    setTimeout(() => scrollToBottom(instant), 150);
    setTimeout(() => scrollToBottom(instant), 350);
    setTimeout(() => scrollToBottom(instant), 600);
  };

  // Image load handler - if image loads and user is at bottom or initial open, stick to bottom
  const handleImageLoad = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const distFromBottom = container.scrollHeight - container.clientHeight - container.scrollTop;
    if (distFromBottom < 450 || isInitialLoadRef.current) {
      scrollToBottom(true);
    }
  };

  // Switch conversation: reset scroll, fetch messages, multi-pass scroll
  useEffect(() => {
    if (activeConversation) {
      const isNewConv = prevActiveConvIdRef.current !== activeConversation._id;
      prevActiveConvIdRef.current = activeConversation._id;

      if (isNewConv) {
        isInitialLoadRef.current = true;
        dispatch(fetchMessages({ conversationId: activeConversation._id, page: 1 }));
        setShowGroupOptions(false);
        executeMultiPassScroll(true);

        const timer = setTimeout(() => {
          isInitialLoadRef.current = false;
        }, 2500);
        return () => clearTimeout(timer);
      }
    }
  }, [activeConversation?._id, dispatch]);

  // Messages update or finished loading: scroll to bottom
  useEffect(() => {
    if (!activeConversation) return;

    if (isInitialLoadRef.current || (messagesPage === 1 && !messagesLoading)) {
      executeMultiPassScroll(true);
    } else {
      const container = messagesContainerRef.current;
      if (container) {
        const distFromBottom = container.scrollHeight - container.clientHeight - container.scrollTop;
        if (distFromBottom < 350) {
          executeMultiPassScroll(false);
        }
      }
    }

    prevMessagesLengthRef.current = messages.length;

    // Mark all messages as read
    socket.emit("mark_messages_read", { conversationId: activeConversation._id });
    dispatch(resetChatUnreadCount({ conversationId: activeConversation._id }));
  }, [activeConversation?._id, messages.length, messagesLoading]);

  // ResizeObserver to detect layout expansion (images loading, fonts, etc.) and keep scrolled to bottom
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    let isNearBottom = true;
    const handleCheckScroll = () => {
      const dist = container.scrollHeight - container.clientHeight - container.scrollTop;
      isNearBottom = dist < 250;
    };

    container.addEventListener("scroll", handleCheckScroll, { passive: true });

    const resizeObserver = new ResizeObserver(() => {
      if (isInitialLoadRef.current || isNearBottom) {
        container.scrollTop = container.scrollHeight;
        setShowScrollToBottom(false);
      }
    });

    resizeObserver.observe(container);

    return () => {
      container.removeEventListener("scroll", handleCheckScroll);
      resizeObserver.disconnect();
    };
  }, [activeConversation?._id]);

  // Fetch file size limit from backend on mount
  useEffect(() => {
    const fetchLimit = async () => {
      try {
        const res = await axios.get(`${BaseUrl}/Chat/file-size-limit`);
        if (res.data && res.data.limit) {
          setFileSizeLimit(res.data.limit);
        }
      } catch (err) {
        console.error("Error fetching file size limit:", err);
      }
    };
    fetchLimit();
  }, []);

  // Handle click outside to close emoji pickers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showEmojiPicker &&
        composerEmojiRef.current &&
        !composerEmojiRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Handle scroll for pagination (Infinite Scrolling)
  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;


    // Scroll-to-bottom visibility: when scrolled up more than 300px from bottom
    const distanceFromBottom = container.scrollHeight - container.clientHeight - container.scrollTop;
    setShowScrollToBottom(distanceFromBottom > 300);

    // When scrolling near the top, load next page
    if (container.scrollTop === 0 && hasMoreMessages && !messagesLoading && activeConversation) {
      const scrollHeightBefore = container.scrollHeight;
      dispatch(fetchMessages({ conversationId: activeConversation._id, page: messagesPage + 1 })).then(() => {
        // Restore scroll position after loading older messages
        setTimeout(() => {
          if (container) {
            container.scrollTop = container.scrollHeight - scrollHeightBefore;
          }
        }, 50);
      });
    }
  };

  // Keyboard and typing trigger for status indicators
  const handleInputChange = (e) => {
    setInputText(e.target.value);

    if (!isTyping && activeConversation) {
      setIsTyping(true);
      socket.emit("typing_status", { conversationId: activeConversation._id, isTyping: true });
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      if (isTyping && activeConversation) {
        setIsTyping(false);
        socket.emit("typing_status", { conversationId: activeConversation._id, isTyping: false });
      }
    }, 1500);
  };

  // Send text message handler
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputText.trim() || !activeConversation) return;

    if (isDynamicManagement) {
      const myMemberDoc = members?.find(m => m._id?.toString() === currentUserId?.toString());
      if (myMemberDoc && myMemberDoc.attendanceBotEnabled === false) {
        alert("Your access to the Attendance Menu has been disabled by the administrator.");
        return;
      }
    }

    const payload = {
      conversationId: activeConversation._id,
      message: inputText.trim(),
      messageType: "text"
    };

    if (replyingToMessage) {
      payload.replyTo = {
        messageId: replyingToMessage._id,
        message: replyingToMessage.message,
        messageType: replyingToMessage.messageType,
        senderName: replyingToMessage.sender?.Name || "Member"
      };
    }

    socket.emit("send_message", payload, (res) => {
      if (res && res.success) {
        setInputText("");
        setShowEmojiPicker(false);
        setReplyingToMessage(null);
        // Clear typing indicator instantly
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        setIsTyping(false);
        socket.emit("typing_status", { conversationId: activeConversation._id, isTyping: false });
      }
    });
  };

  // Handle image/file selection - trigger WhatsApp style confirmation modal
  const handleImageUpload = (e) => {
    const rawFiles = e.target.files;
    if (!rawFiles || rawFiles.length === 0 || !activeConversation) return;

    const files = Array.from(rawFiles);
    let totalBytes = 0;

    for (const f of files) {
      if (f.size > fileSizeLimit * 1024 * 1024) {
        alert(`File "${f.name}" exceeds the limit of ${fileSizeLimit}MB set by the administrator.`);
        return;
      }
      totalBytes += f.size;
    }

    setPendingSendFiles(files);
    setPendingSendFile(files[0]);
    setPendingTotalFileSize(totalBytes);

    const isFirstImage = files[0].type.startsWith("image/");
    if (isFirstImage && files.length === 1) {
      setFilePreviewUrl(URL.createObjectURL(files[0]));
    } else {
      setFilePreviewUrl("");
    }
    setPendingCaption("");
    setIsConfirmingSend(true);
  };

  // Handle clipboard image paste
  const handleInputPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          e.preventDefault();

          // Validate file size using dynamic limit
          if (file.size > fileSizeLimit * 1024 * 1024) {
            alert(`Pasted image size exceeds the limit of ${fileSizeLimit}MB set by the administrator.`);
            return;
          }

          setPendingSendFiles([file]);
          setPendingSendFile(file);
          setPendingTotalFileSize(file.size);
          setFilePreviewUrl(URL.createObjectURL(file));
          setPendingCaption("");
          setIsConfirmingSend(true);
          break;
        }
      }
    }
  };

  // Upload and send file/image with caption
  const confirmSendFile = async (e) => {
    if (e) e.preventDefault();
    const files = pendingSendFiles && pendingSendFiles.length > 0 ? pendingSendFiles : (pendingSendFile ? [pendingSendFile] : []);
    if (files.length === 0 || !activeConversation) return;

    const isSingleImage = files.length === 1 && files[0].type.startsWith("image/");
    const captionText = pendingCaption.trim();

    const formData = new FormData();
    files.forEach((f) => {
      formData.append("files", f);
      formData.append("file", f);
    });

    setIsConfirmingSend(false);
    setPendingSendFiles([]);
    setPendingSendFile(null);
    setFilePreviewUrl("");
    setPendingCaption("");

    try {
      setIsUploading(true);
      const response = await axios.post(`${BaseUrl}/Chat/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      if (response.data && response.data.status === "success") {
        const fileUrl = response.data.url;
        const messageType = isSingleImage ? "image" : "file";

        // Emit new message via socket with caption and file size details
        const payload = {
          conversationId: activeConversation._id,
          message: fileUrl,
          messageType: messageType,
          caption: captionText || undefined,
          fileSize: response.data.fileSize,
          totalFileSize: response.data.totalFileSize,
          fileCount: response.data.fileCount || files.length,
          files: response.data.files
        };

        if (replyingToMessage) {
          payload.replyTo = {
            messageId: replyingToMessage._id || replyingToMessage.messageId,
            message: replyingToMessage.message,
            messageType: replyingToMessage.messageType,
            senderName: replyingToMessage.sender?.Name || "Member"
          };
          setReplyingToMessage(null);
        }

        socket.emit("send_message", payload);
      } else {
        alert("Failed to upload file. Please try again.");
      }
    } catch (error) {
      console.error("File upload error:", error);
      alert(error.response?.data?.message || "Failed to upload file.");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Download file helper from URL via proxy
  const handleDownloadImage = (msg) => {
    const url = typeof msg === 'string' ? msg : msg.message;
    const createdAt = typeof msg === 'string' ? null : msg.createdAt;

    const downloadUrl = `${BaseUrl}/Chat/download-file?url=${encodeURIComponent(url)}`;
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = getFileName(url, createdAt);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Start new one-to-one conversation from contacts list
  const handleContactSelect = (contact) => {
    if (!currentUserId) return;
    dispatch(
      createOrGetConversation({
        participants: [currentUserId, contact._id],
        isGroup: false,
        userId: currentUserId
      })
    ).then(() => {
      setActiveTab("chats");
      setSearchQuery("");
    });
  };

  // Format timestamp nicely (SaaS style)
  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Format date separators
  const formatDateLabel = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
    }
  };

  // Format last seen timestamp with date details
  const formatLastSeen = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const timeStr = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    if (date.toDateString() === today.toDateString()) {
      return `last seen today at ${timeStr}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `last seen yesterday at ${timeStr}`;
    } else {
      const dateLabel = date.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
      return `last seen on ${dateLabel} at ${timeStr}`;
    }
  };


  // Group options handlers
  const handleLeaveGroup = () => {
    if (window.confirm("Are you sure you want to leave this group?")) {
      dispatch(
        manageGroupChat({
          conversationId: activeConversation._id,
          action: "leave",
          userId: currentUserId
        })
      ).then(() => {
        dispatch(setActiveConversation(null));
        setShowGroupOptions(false);
      });
    }
  };

  // Search filter with Pinning logic
  const filteredConversations = conversations
    .filter((c) => {
      // ALWAYS show Dynamic Management chat by default, even if it has no messages yet!
      if (isDynamicManagementConv(c)) return true;

      // Hide other non-group chats with no messages
      if (!c.lastMessage && !c.isGroup) return false;

      if (c.isGroup) {
        return c.name.toLowerCase().includes(searchQuery.toLowerCase());
      } else {
        const recipient = getRecipientInfo(c);
        return recipient?.Name?.toLowerCase().includes(searchQuery.toLowerCase());
      }
    })
    .sort((a, b) => {
      const aIsDM = isDynamicManagementConv(a);
      const bIsDM = isDynamicManagementConv(b);
      if (aIsDM && !bIsDM) return -1;
      if (!aIsDM && bIsDM) return 1;

      const aIsPin = pinnedConvIds.includes(a._id);
      const bIsPin = pinnedConvIds.includes(b._id);
      if (aIsPin && !bIsPin) return -1;
      if (!aIsPin && bIsPin) return 1;

      const aTime = a.lastMessage ? new Date(a.lastMessage.createdAt).getTime() : 0;
      const bTime = b.lastMessage ? new Date(b.lastMessage.createdAt).getTime() : 0;
      return bTime - aTime;
    });

  const filteredContacts = members.filter(
    (m) => m._id !== currentUserId && m.Name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Read ticks calculations
  const renderMessageStatus = (msg) => {
    const senderId = msg.sender?._id || msg.sender;
    const isSender = senderId?.toString() === currentUserId?.toString();
    if (!isSender) return null;

    const recipientIds = (activeConversation?.participants || [])
      .map((p) => p._id || p)
      .filter((id) => id && id.toString() !== currentUserId?.toString());

    const seenBy = msg.seenBy || [];
    const deliveredTo = msg.deliveredTo || [];

    const isRead = recipientIds.length > 0 && recipientIds.every((id) => seenBy.some((s) => (s?.userId?._id || s?.userId)?.toString() === id.toString()));
    const isDelivered = recipientIds.length > 0 && recipientIds.every((id) => deliveredTo.some((d) => (d?.userId?._id || d?.userId)?.toString() === id.toString()));

    if (isRead) {
      return (
        <span className="inline-flex items-center ml-1 text-[#38bdf8] shrink-0" title="Read">
          <svg viewBox="0 0 16 11" width="14" height="9.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
            <path d="M11.07 1.25L4.57 8.75L1.57 5.75" />
            <path d="M14.57 1.25L8.07 8.75L6.57 7.25" />
          </svg>
        </span>
      );
    } else if (isDelivered) {
      return (
        <span className="inline-flex items-center ml-1 text-white/70 shrink-0" title="Delivered">
          <svg viewBox="0 0 16 11" width="14" height="9.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
            <path d="M11.07 1.25L4.57 8.75L1.57 5.75" />
            <path d="M14.57 1.25L8.07 8.75L6.57 7.25" />
          </svg>
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center ml-1 text-white/55 shrink-0" title="Sent">
          <svg viewBox="0 0 16 11" width="12" height="9.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
            <path d="M11.07 1.25L4.57 8.75L1.57 5.75" />
          </svg>
        </span>
      );
    }
  };

  // Return helper: check online status of individual chat
  const isRecipientOnline = () => {
    if (!activeConversation || activeConversation.isGroup) return false;
    const recipient = getRecipientInfo(activeConversation);
    return onlineUsers[recipient?._id] || false;
  };

  // Helper: check if anyone is typing in active conversation
  const getTypingText = () => {
    if (!activeConversation) return "";
    const activeTyping = typingUsers[activeConversation._id];
    if (!activeTyping) return "";

    const typists = Object.keys(activeTyping)
      .filter((uid) => uid !== currentUserId && activeTyping[uid])
      .map((uid) => {
        const member = activeConversation.participants.find((p) => (p._id || p) === uid);
        return member ? member.Name : "";
      })
      .filter(Boolean);

    if (typists.length === 0) return "";
    if (typists.length === 1) return `${typists[0]} is typing...`;
    return "Multiple people typing...";
  };

  if (!isChatDrawerOpen || !chatEnabled) return null;

  return (
    <div
      className="chat-drawer-overlay"
      onClick={() => dispatch(setChatDrawerOpen(false))}
    >
      <div
        className="chat-drawer-container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Drawer */}
        <div className="chat-drawer-header">
          <div className="flex items-center gap-3">
            <button
              onClick={() => dispatch(setChatDrawerOpen(false))}
              className="text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors cursor-pointer border-none bg-transparent"
            >
              <ArrowLeft size={20} />
            </button>
            <h2 className="text-lg font-bold tracking-wide">Messaging System</h2>
          </div>
          <button
            onClick={() => dispatch(setChatDrawerOpen(false))}
            className="text-white/70 hover:text-white p-1 rounded hover:bg-white/10 transition-colors border-none bg-transparent cursor-pointer"
          >
            <X size={20} />
          </button>
        </div>

        <div className="chat-drawer-body">
          {/* List Section (Conversations & Contacts) */}
          <div className={`chat-list-panel ${activeConversation ? "hidden md:flex" : "flex"}`}>
            {/* Top Profile Header */}
            <div className="chat-sidebar-profile-header">
              <div className="chat-my-profile">
                <img
                  src={myMemberInfo?.ProfilePhoto || authData?.data?.ProfilePhoto || defaultProfile}
                  alt="My Avatar"
                  className="chat-my-avatar"
                  onError={(e) => {
                    e.target.src = defaultProfile;
                  }}
                />
                <div className="chat-my-info">
                  <span className="chat-my-name truncate">{authData?.data?.Name || "Me"}</span>
                  <span className="chat-my-role truncate">{authData?.data?.Role || "User"}</span>
                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="chat-tabs-bar">
              <button
                onClick={() => {
                  setActiveTab("chats");
                  setSearchQuery("");
                }}
                className={`chat-tab-btn ${activeTab === "chats" ? "active" : ""}`}
              >
                Chats
              </button>
              <button
                onClick={() => {
                  setActiveTab("contacts");
                  setSearchQuery("");
                }}
                className={`chat-tab-btn ${activeTab === "contacts" ? "active" : ""}`}
              >
                Contacts ({filteredContacts.length})
              </button>
            </div>

            {/* Search Input */}
            <div className="chat-search-wrapper" style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <Search size={15} style={{ position: "absolute", left: "28px", color: "#94a3b8", pointerEvents: "none" }} />
              <input
                type="text"
                placeholder={activeTab === "chats" ? "Search conversations..." : "Search members..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="chat-search-input"
                style={{ width: "100%", paddingLeft: "38px" }}
              />
            </div>

            {/* Scrollable list */}
            <div className="chat-scrollable-list">
              {activeTab === "chats" ? (
                /* Conversations List */
                filteredConversations.length > 0 ? (
                  filteredConversations.slice(0, 100).map((conv) => {
                    const recipient = getRecipientInfo(conv);
                    const isOnline = !conv.isGroup && onlineUsers[recipient?._id];
                    const isTypingActive = typingUsers[conv._id] &&
                      Object.keys(typingUsers[conv._id]).some(uid => uid !== currentUserId && typingUsers[conv._id][uid]);
                    const isDM = isDynamicManagementConv(conv);
                    const isPinned = pinnedConvIds.includes(conv._id);

                    return (
                      <div
                        key={conv._id}
                        onClick={() => dispatch(setActiveConversation(conv))}
                        className={`chat-item-row ${activeConversation?._id === conv._id ? "active" : ""}`}
                      >
                        {/* Avatar */}
                        <div className="chat-avatar-container">
                          <img
                            src={conv.isGroup ? (conv.groupImage || defaultProfile) : (recipient?.ProfilePhoto || defaultProfile)}
                            alt="Avatar"
                            className="chat-avatar"
                            onError={(e) => {
                              e.target.src = defaultProfile;
                            }}
                          />
                          {isOnline && <span className="online-badge-dot"></span>}
                        </div>

                        {/* Text Snippets */}
                        <div className="chat-item-content">
                          <div className="chat-item-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h4 className="chat-item-name" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              {conv.isGroup ? conv.name : recipient?.Name}
                            </h4>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              {!isDM && isPinned && (
                                <Pin size={12} style={{ color: "#f59e0b", fill: "#f59e0b", transform: "rotate(45deg)" }} title="Pinned Chat" />
                              )}
                              <span className="chat-item-time">
                                {conv.lastMessage ? formatTime(conv.lastMessage.createdAt) : ""}
                              </span>
                              {!isDM && (
                                <button
                                  type="button"
                                  onClick={(e) => togglePinConv(conv._id, e)}
                                  title={isPinned ? "Unpin chat" : "Pin chat"}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: "2px",
                                    color: isPinned ? "#f59e0b" : "#94a3b8",
                                    display: "flex",
                                    alignItems: "center"
                                  }}
                                >
                                  {isPinned ? <PinOff size={13} /> : <Pin size={13} />}
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="chat-item-details">
                            <p className={`chat-item-snippet ${isTypingActive ? "typing" : ""}`}>
                              {isTypingActive ? (
                                "typing..."
                              ) : conv.lastMessage ? (
                                conv.lastMessage.messageType === "announcement" ? (
                                  `📢 ${conv.lastMessage.message}`
                                ) : conv.lastMessage.messageType === "image" ? (
                                  "📷 Image"
                                ) : (
                                  conv.lastMessage.message
                                )
                              ) : (
                                <span className="italic text-gray-400">No messages yet</span>
                              )}
                            </p>

                            {/* Unread badge count */}
                            {conv.unreadCount > 0 && (
                              <span className="chat-unread-badge">{conv.unreadCount}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="chat-empty-state">
                    <span className="text-3xl mb-1">💬</span>
                    <p className="text-sm font-medium text-gray-500">No active conversations</p>
                  </div>
                )
              ) : (
                /* Contacts List */
                filteredContacts.length > 0 ? (
                  filteredContacts.map((contact) => {
                    const isOnline = onlineUsers[contact._id];
                    return (
                      <div
                        key={contact._id}
                        onClick={() => handleContactSelect(contact)}
                        className="chat-item-row"
                      >
                        <div className="chat-avatar-container">
                          <img
                            src={contact.ProfilePhoto || defaultProfile}
                            alt={contact.Name}
                            className="chat-avatar"
                            onError={(e) => {
                              e.target.src = defaultProfile;
                            }}
                          />
                          {isOnline && <span className="online-badge-dot"></span>}
                        </div>
                        <div className="chat-item-content">
                          <h4 className="chat-item-name">{contact.Name}</h4>
                          <p className="chat-item-snippet text-xs">{contact.Role}</p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="chat-empty-state">
                    <span className="text-3xl mb-1">👥</span>
                    <p className="text-sm font-medium text-gray-500">No active members found</p>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Active Conversation Panel */}
          {activeConversation ? (
            <div className={`chat-conversation-panel ${activeConversation ? "flex" : "hidden"}`} style={{ position: "relative" }}>
              {/* Header */}
              <div className="chat-conv-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", paddingRight: "16px" }}>
                <div className="chat-header-user-info min-w-0" style={{ display: "flex", alignItems: "center", gap: "12.5px", flex: 1 }}>
                  <button
                    onClick={() => dispatch(setActiveConversation(null))}
                    className="md:hidden chat-back-btn hover:bg-slate-200/50 p-1.5 rounded-lg transition-colors cursor-pointer border-none bg-transparent"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <img
                    src={
                      activeConversation.isGroup
                        ? activeConversation.groupImage || defaultProfile
                        : getRecipientInfo(activeConversation)?.ProfilePhoto || defaultProfile
                    }
                    alt="Avatar"
                    className="chat-conv-avatar"
                    onError={(e) => {
                      e.target.src = defaultProfile;
                    }}
                  />
                  <div className="chat-conv-details min-w-0">
                    <h3 className="chat-conv-name truncate">
                      {activeConversation.isGroup ? activeConversation.name : getRecipientInfo(activeConversation)?.Name}
                    </h3>
                    <p
                      title={activeConversation.isGroup ? activeConversation.participants.map(p => p.Name).filter(Boolean).join(", ") : ""}
                      className={`chat-conv-status truncate ${getTypingText() || (!activeConversation.isGroup && isRecipientOnline() && !isDynamicManagement) ? "online" : ""}`}
                    >
                      {getTypingText() || (
                        activeConversation.isGroup ? (
                          `${activeConversation.participants.length} members: ${activeConversation.participants.map(p => p.Name).filter(Boolean).join(", ")}`
                        ) : isRecipientOnline() ? (
                          isDynamicManagement ? "" : "online"
                        ) : (
                          getRecipientInfo(activeConversation)?.lastSeen && !isDynamicManagement ? (
                            formatLastSeen(getRecipientInfo(activeConversation).lastSeen)
                          ) : (
                            isDynamicManagement ? "" : "offline"
                          )
                        )
                      )}
                    </p>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center" }}>
                  {/* Search inside chat button */}
                  <button
                    type="button"
                    onClick={() => {
                      setShowInChatSearch(!showInChatSearch);
                      if (showInChatSearch) setInChatSearchQuery("");
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: showInChatSearch ? "#2563eb" : "#1e3a8a",
                      padding: "8px",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: showInChatSearch ? "#dbeafe" : "#eff6ff",
                      transition: "all 0.2s",
                      marginRight: "6px"
                    }}
                    title="Search messages in this chat"
                  >
                    <Search size={16} />
                  </button>

                  {/* Pin conversation button */}
                  {!isDynamicManagementConv(activeConversation) && (
                    <button
                      type="button"
                      onClick={(e) => togglePinConv(activeConversation._id, e)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: pinnedConvIds.includes(activeConversation._id) ? "#d97706" : "#1e3a8a",
                        padding: "8px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: pinnedConvIds.includes(activeConversation._id) ? "#fef3c7" : "#eff6ff",
                        transition: "all 0.2s",
                        marginRight: "6px"
                      }}
                      title={pinnedConvIds.includes(activeConversation._id) ? "Unpin conversation" : "Pin conversation"}
                    >
                      <Pin size={16} style={{ fill: pinnedConvIds.includes(activeConversation._id) ? "#d97706" : "none" }} />
                    </button>
                  )}

                  {activeConversation.isGroup && (
                    <button
                      type="button"
                      onClick={() => setShowMembersModal(true)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#1e3a8a",
                        padding: "8px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#eff6ff",
                        transition: "all 0.2s"
                      }}
                      title="View Group Members"
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#dbeafe"}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#eff6ff"}
                    >
                      <Users size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* In-Chat Message Search Bar */}
              {showInChatSearch && (
                <div className="in-chat-search-bar" style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 12px",
                  backgroundColor: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                  gap: "8px",
                  position: "relative",
                  zIndex: 10
                }}>
                  <Search size={15} style={{ color: "#94a3b8" }} />
                  <input
                    type="text"
                    placeholder="Search messages in this chat..."
                    value={inChatSearchQuery}
                    onChange={(e) => setInChatSearchQuery(e.target.value)}
                    autoFocus
                    style={{
                      flex: 1,
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      padding: "6px 10px",
                      fontSize: "13px",
                      outline: "none"
                    }}
                  />
                  <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 600, whiteSpace: "nowrap" }}>
                    {inChatSearchQuery.trim()
                      ? matchingMessages.length > 0
                        ? `${currentSearchMatchIndex + 1} of ${matchingMessages.length}`
                        : "0 matches"
                      : ""}
                  </span>
                  <button
                    type="button"
                    onClick={handlePrevMatch}
                    disabled={matchingMessages.length === 0}
                    style={{ background: "none", border: "none", cursor: matchingMessages.length > 0 ? "pointer" : "default", opacity: matchingMessages.length > 0 ? 1 : 0.4 }}
                    title="Previous Match"
                  >
                    <ChevronUp size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={handleNextMatch}
                    disabled={matchingMessages.length === 0}
                    style={{ background: "none", border: "none", cursor: matchingMessages.length > 0 ? "pointer" : "default", opacity: matchingMessages.length > 0 ? 1 : 0.4 }}
                    title="Next Match"
                  >
                    <ChevronDown size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowInChatSearch(false);
                      setInChatSearchQuery("");
                    }}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }}
                    title="Close Search"
                  >
                    <X size={18} />
                  </button>
                </div>
              )}

              {/* Subtle Brand Logo Watermark Overlay */}
              <div
                className="chat-watermark-overlay"
                style={{
                  position: "absolute",
                  top: "55%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  opacity: 0.11,
                  pointerEvents: "none",
                  zIndex: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                }}
              >
                <img
                  src={mainLogo}
                  alt="Watermark Logo"
                  style={{
                    maxWidth: "260px",
                    maxHeight: "260px",
                    objectFit: "contain"
                  }}
                />
              </div>

              {/* Message scroll list */}
              <div
                ref={messagesContainerRef}
                onScroll={handleScroll}
                className="chat-messages-viewport"
                style={{ position: "relative", zIndex: 1 }}
              >
                {messagesLoading && messagesPage === 1 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="chat-spinner"></div>
                  </div>
                ) : (
                  <>
                    {messages.map((msg, index) => {
                      const senderId = msg.sender?._id || msg.sender;
                      const isSender = senderId?.toString() === currentUserId?.toString();
                      const showDateLabel = index === 0 ||
                        new Date(msg.createdAt).toDateString() !== new Date(messages[index - 1].createdAt).toDateString();

                      return (
                        <React.Fragment key={msg._id || index}>
                          {showDateLabel && (
                            <div className="chat-date-container">
                              <span className="chat-date-label">
                                {formatDateLabel(msg.createdAt)}
                              </span>
                            </div>
                          )}

                          {msg.messageType === "announcement" ? (
                            /* Center announcements */
                            <div className="chat-announcement-container">
                              <div className="chat-announcement-bubble">
                                <span>📢 {msg.message}</span>
                                <span className="chat-announcement-time">
                                  {formatTime(msg.createdAt)}
                                </span>
                              </div>
                            </div>
                          ) : (
                            /* Normal user messages */
                            <div
                              id={`msg-${msg._id}`}
                              className={`saas-message-row ${isSender ? "sender" : "receiver"}`}
                              style={{
                                position: "relative",
                                marginBottom: msg.reactions && msg.reactions.length > 0 ? "24px" : "12px"
                              }}
                              onMouseEnter={() => setHoveredMessageId(msg._id)}
                              onMouseLeave={() => setHoveredMessageId(null)}
                            >
                              {/* Avatar display beside the bubble */}
                              <img
                                src={msg.sender?.ProfilePhoto || (isSender ? authData?.data?.ProfilePhoto : getRecipientInfo(activeConversation)?.ProfilePhoto) || defaultProfile}
                                alt="Sender Avatar"
                                className="saas-message-avatar"
                                onError={(e) => {
                                  e.target.src = defaultProfile;
                                }}
                              />

                              <div style={{ position: "relative", display: "flex", alignItems: "center", maxWidth: window.innerWidth < 640 ? "88%" : "65%", flexDirection: isSender ? "row-reverse" : "row" }}>
                                <div
                                  className={`saas-message-bubble ${isSender ? "sender" : "receiver"}`}
                                  style={{
                                    maxWidth: "100%",
                                    minWidth: msg.replyTo ? "200px" : "90px",
                                    ...(!isSender && isDynamicManagement ? {
                                      background: "linear-gradient(135deg, #f8fafc 0%, #f0f7ff 100%)",
                                      border: "1px solid rgba(226, 232, 240, 0.8)",
                                      borderRadius: "16px 16px 16px 4px",
                                      boxShadow: "0 4px 16px rgba(37, 99, 235, 0.06)",
                                      padding: "10px 14px 8px 14px"
                                    } : {})
                                  }}
                                >
                                  {msg.isForwarded && (
                                    <span style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      fontSize: "10px",
                                      color: isSender ? "rgba(255,255,255,0.7)" : "#64748b",
                                      fontStyle: "italic",
                                      marginBottom: "4px"
                                    }}>
                                      <CornerUpRight size={10} /> Forwarded
                                    </span>
                                  )}
                                  {!isSender && activeConversation.isGroup && (
                                    <span className="saas-sender-name">
                                      {msg.sender?.Name || "Member"}
                                    </span>
                                  )}

                                  {msg.replyTo && (
                                    <div
                                      onClick={() => scrollToMessage(msg.replyTo.messageId)}
                                      style={{
                                        backgroundColor: isSender ? "rgba(255, 255, 255, 0.15)" : "#f1f5f9",
                                        borderLeft: `3px solid ${isSender ? "#fff" : "#1e3a8a"}`,
                                        padding: "6px 10px",
                                        borderRadius: "4px",
                                        marginBottom: "6px",
                                        cursor: "pointer",
                                        fontSize: "12px",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "2px",
                                        maxWidth: "100%",
                                        overflow: "hidden"
                                      }}
                                    >
                                      <span style={{
                                        fontWeight: 700,
                                        color: isSender ? "#fff" : "#1e3a8a",
                                        fontSize: "11px"
                                      }}>
                                        {msg.replyTo.senderName}
                                      </span>
                                      <span style={{
                                        color: isSender ? "rgba(255, 255, 255, 0.85)" : "#475569",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis"
                                      }}>
                                        {msg.replyTo.messageType === "image" ? "📷 Image" : msg.replyTo.messageType === "file" ? "📁 Document" : msg.replyTo.message}
                                      </span>
                                    </div>
                                  )}

                                  {msg.messageType === "image" ? (
                                    <div className="flex flex-col">
                                      <div className="saas-image-container" style={{ position: "relative", display: "inline-block" }}>
                                        <img
                                          src={msg.message}
                                          alt="Message Image"
                                          className="saas-message-image cursor-pointer hover:opacity-90 transition-all"
                                          style={{ display: "block", cursor: "pointer" }}
                                          onLoad={handleImageLoad}
                                          onClick={() => setLightboxImage(msg.message)}
                                        />
                                        <button
                                          type="button"
                                          onClick={() => handleDownloadImage(msg)}
                                          className="saas-image-download-btn animate__animated animate__fadeIn"
                                          title="Download Image"
                                          style={{
                                            position: "absolute",
                                            top: "10px",
                                            right: "10px",
                                            backgroundColor: "rgba(15, 23, 42, 0.65)",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "8px",
                                            padding: "8.5px",
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            transition: "all 0.2s ease-out",
                                            zIndex: 5
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = "#133a69";
                                            e.currentTarget.style.transform = "scale(1.08)";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = "rgba(15, 23, 42, 0.65)";
                                            e.currentTarget.style.transform = "scale(1)";
                                          }}
                                        >
                                          <Download size={15} />
                                        </button>
                                      </div>
                                      {msg.caption && renderExpandableText(msg.caption, msg._id, true, isSender)}
                                    </div>
                                  ) : msg.messageType === "file" ? (
                                    <div className="flex flex-col">
                                      <div className="saas-file-attachment-card flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg p-2.5 max-w-[270px]">
                                        <div className="p-2 bg-blue-50 text-blue-600 rounded-lg shrink-0">
                                          <FileText size={24} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs font-bold text-slate-800 truncate m-0">
                                            {getFileName(msg.message, msg.createdAt)}
                                          </p>
                                          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                                            <span className="text-[10px] text-slate-500 uppercase font-semibold">
                                              {getFileExtension(msg.message)}
                                            </span>
                                            {(msg.fileSize || msg.totalFileSize) && (
                                              <>
                                                <span className="text-[10px] text-slate-300">•</span>
                                                <span className="text-[10px] text-slate-600 font-bold">
                                                  {formatFileSize(msg.fileSize || msg.totalFileSize)}
                                                </span>
                                              </>
                                            )}
                                            {msg.fileCount > 1 && (
                                              <span className="text-[9px] bg-blue-100 text-blue-700 font-bold px-1.5 py-0.5 rounded">
                                                {msg.fileCount} Files
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex flex-col gap-1.5 shrink-0">
                                          <button
                                            type="button"
                                            onClick={() => handleDownloadImage(msg)}
                                            className="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md border-none cursor-pointer transition-all flex items-center justify-center"
                                            title="View/Open File"
                                          >
                                            <ExternalLink size={12} />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleDownloadImage(msg)}
                                            className="p-1.5 bg-slate-600 hover:bg-slate-700 text-white rounded-md border-none cursor-pointer transition-all flex items-center justify-center"
                                            title="Download File"
                                          >
                                            <Download size={12} />
                                          </button>
                                        </div>
                                      </div>
                                      {msg.caption && renderExpandableText(msg.caption, msg._id, true, isSender)}
                                    </div>
                                  ) : (
                                    renderExpandableText(msg.message, msg._id, false, isSender)
                                  )}

                                  <div className="saas-message-footer">
                                    <span className="saas-message-time">{formatTime(msg.createdAt)}</span>
                                    {isSender && renderMessageStatus(msg)}
                                  </div>
                                </div>

                                {/* Reactions display */}
                                {msg.reactions && msg.reactions.length > 0 && (
                                  <div style={{
                                    position: "absolute",
                                    bottom: "-12px",
                                    [isSender ? "right" : "left"]: "15px",
                                    display: "flex",
                                    gap: "2px",
                                    backgroundColor: "white",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "12px",
                                    padding: "2px 6px",
                                    fontSize: "12px",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                    zIndex: 2
                                  }}>
                                    {Object.entries(msg.reactions.reduce((acc, r) => {
                                      acc[r.emoji] = (acc[r.emoji] || 0) + 1;
                                      return acc;
                                    }, {})).map(([emoji, count]) => (
                                      <span key={emoji} style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                                        {emoji} <span style={{ fontSize: "10px", color: "#64748b" }}>{count > 1 ? count : ""}</span>
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {/* Hover Action Controls (React, Forward, Reply) */}
                                {hoveredMessageId === msg._id && !activeReactionMessageId && !isDynamicManagementConv(activeConversation) && (
                                  <div
                                    className="animate__animated animate__fadeIn"
                                    style={{
                                      position: "absolute",
                                      [isSender ? "right" : "left"]: "calc(100% + 8px)",
                                      top: "50%",
                                      transform: "translateY(-50%)",
                                      display: "flex",
                                      gap: "4px",
                                      alignItems: "center",
                                      zIndex: 10
                                    }}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => setActiveReactionMessageId(msg._id)}
                                      style={{
                                        background: "white",
                                        border: "1px solid #e2e8f0",
                                        borderRadius: "50%",
                                        width: "28px",
                                        height: "28px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        color: "#64748b",
                                        boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                                        borderStyle: "solid",
                                        borderWidth: "1px"
                                      }}
                                      title="React"
                                    >
                                      <Smile size={16} />
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => {
                                        setMessageToForward(msg);
                                        setShowForwardModal(true);
                                      }}
                                      style={{
                                        background: "white",
                                        border: "1px solid #e2e8f0",
                                        borderRadius: "50%",
                                        width: "28px",
                                        height: "28px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        color: "#64748b",
                                        boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                                        borderStyle: "solid",
                                        borderWidth: "1px"
                                      }}
                                      title="Forward"
                                    >
                                      <CornerUpRight size={16} />
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => setReplyingToMessage(msg)}
                                      style={{
                                        background: "white",
                                        border: "1px solid #e2e8f0",
                                        borderRadius: "50%",
                                        width: "28px",
                                        height: "28px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        color: "#64748b",
                                        boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                                        borderStyle: "solid",
                                        borderWidth: "1px"
                                      }}
                                      title="Reply"
                                    >
                                      <CornerUpLeft size={16} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {getTypingText() && (
                      <div className="flex items-end gap-2 mb-4 animate__animated animate__fadeIn" style={{ alignSelf: "flex-start" }}>
                        {!activeConversation.isGroup && (
                          <img
                            src={getRecipientInfo(activeConversation)?.ProfilePhoto || defaultProfile}
                            alt="Avatar"
                            className="w-[30px] h-[30px] rounded-full shrink-0 object-cover"
                            onError={(e) => { e.target.src = defaultProfile; }}
                          />
                        )}
                        <div
                          className="rounded-2xl rounded-bl-none px-4 py-3 shadow-[0_2px_8px_rgba(0,0,0,0.05)]"
                          style={{
                            border: "1px solid #e2e8f0",
                            background: "rgba(248, 250, 252, 0.9)",
                            backdropFilter: "blur(4px)",
                            display: "flex",
                            alignItems: "center"
                          }}
                        >
                          <style>{`
                            @keyframes typing-bounce {
                              0%, 100% { transform: translateY(0) scale(1); background-color: #cbd5e1; }
                              50% { transform: translateY(-5px) scale(1.18); background-color: #4f46e5; }
                            }
                            .typing-dot {
                              display: inline-block;
                              animation: typing-bounce 1.2s infinite ease-in-out;
                            }
                          `}</style>
                          <div className="flex items-center gap-1.5">
                            <span className="w-[9px] h-[9px] rounded-full typing-dot" style={{ animationDelay: '0s' }} />
                            <span className="w-[9px] h-[9px] rounded-full typing-dot" style={{ animationDelay: '0.18s' }} />
                            <span className="w-[9px] h-[9px] rounded-full typing-dot" style={{ animationDelay: '0.36s' }} />
                          </div>
                        </div>
                      </div>
                    )}
                    <div style={{ height: "20px", width: "100%", clear: "both", flexShrink: 0 }} />
                    <div ref={messagesEndRef} style={{ height: "1px", width: "100%", clear: "both" }} />
                  </>
                )}
              </div>

              {/* Floating Scroll Buttons */}
              <div style={{
                position: "absolute",
                bottom: replyingToMessage ? "145px" : "80px",
                right: "20px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                zIndex: 10,
                pointerEvents: "none"
              }}>

                {showScrollToBottom && (
                  <button
                    type="button"
                    onClick={scrollToBottom}
                    style={{
                      pointerEvents: "auto",
                      backgroundColor: "white",
                      border: "1px solid #cbd5e1",
                      borderRadius: "50%",
                      width: "36px",
                      height: "36px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#1e3a8a",
                      cursor: "pointer",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                      transition: "all 0.2s ease-in-out",
                      opacity: 0.85
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = "1";
                      e.currentTarget.style.transform = "scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = "0.85";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                    title="Go to Bottom"
                  >
                    <ChevronDown size={20} />
                  </button>
                )}
              </div>

              {/* Message input Composer bar */}
              <div className="chat-composer-bar">
                {isDynamicManagement ? (
                  <div className="flex flex-col w-full gap-2">
                    <form onSubmit={handleSendMessage} className="flex items-center gap-2 sm:gap-3 w-full">
                      <button
                        type="button"
                        className="chat-composer-send-btn active w-10 sm:w-auto px-0 sm:px-4"
                        style={{
                          background: themes[currentTheme]?.bubble || "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                          cursor: "pointer",
                          color: "white",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "700",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "40px",
                          flexShrink: 0,
                          whiteSpace: "nowrap",
                          boxShadow: themes[currentTheme]?.isDark
                            ? "0 4px 12px rgba(0,0,0,0.5)"
                            : `0 4px 12px ${(themes[currentTheme]?.primary) || "#1e3a8a"}33`,
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-1.5px) scale(1.02)";
                          e.currentTarget.style.boxShadow = themes[currentTheme]?.isDark
                            ? "0 6px 18px rgba(0,0,0,0.7)"
                            : `0 6px 18px ${(themes[currentTheme]?.primary) || "#1e3a8a"}55`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "none";
                          e.currentTarget.style.boxShadow = themes[currentTheme]?.isDark
                            ? "0 4px 12px rgba(0,0,0,0.5)"
                            : `0 4px 12px ${(themes[currentTheme]?.primary) || "#1e3a8a"}33`;
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          const myMemberDoc = members?.find(m => m._id?.toString() === currentUserId?.toString());
                          if (myMemberDoc && myMemberDoc.attendanceBotEnabled === false) {
                            alert("Your access to the Attendance Menu has been disabled by the administrator.");
                            return;
                          }
                          socket.emit("send_message", {
                            conversationId: activeConversation._id,
                            message: "Attendance",
                            messageType: "text"
                          });
                        }}
                        title="Attendance Menu"
                      >
                        <Calendar size={15} className="flex-shrink-0" />
                        <span className="hidden sm:inline ml-1.5">Attendance Menu</span>
                      </button>
                      <input
                        type="text"
                        placeholder="Type 'Attendance' or option numbers..."
                        value={inputText}
                        onChange={handleInputChange}
                        className="chat-composer-input flex-1 min-w-0"
                      />
                      <button
                        type="submit"
                        disabled={!inputText.trim()}
                        className={`chat-composer-send-btn flex-shrink-0 ${inputText.trim() ? "active" : ""}`}
                        style={{ height: "40px", width: "40px" }}
                      >
                        <Send size={18} />
                      </button>
                    </form>
                  </div>
                ) : sendEnabled ? (
                  <div className="flex flex-col w-full gap-2">
                    {/* Reply Preview Bar */}
                    {replyingToMessage && (
                      <div className="animate__animated animate__fadeIn" style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        backgroundColor: "#f8fafc",
                        borderLeft: "4px solid #1e3a8a",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        fontSize: "13px",
                        marginBottom: "4px"
                      }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "2px", overflow: "hidden" }}>
                          <span style={{ fontWeight: 700, color: "#1e3a8a", fontSize: "12px" }}>
                            Replying to {replyingToMessage.sender?.Name || "Member"}
                          </span>
                          <span style={{ color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {replyingToMessage.messageType === "image" ? "📷 Image" : replyingToMessage.messageType === "file" ? "📁 Document" : replyingToMessage.message}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setReplyingToMessage(null)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#64748b",
                            padding: "4px"
                          }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}

                    <form onSubmit={handleSendMessage} className="flex items-center gap-2 sm:gap-3 w-full">
                      {/* Hidden S3 File Upload Input */}
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="*"
                        multiple
                        onChange={handleImageUpload}
                        style={{ display: "none" }}
                      />

                      {/* Attachment button */}
                      <button
                        type="button"
                        className="chat-composer-icon-btn flex-shrink-0"
                        onClick={() => fileInputRef.current?.click()}
                        title="Attach File"
                        disabled={isUploading}
                      >
                        <Paperclip size={18} />
                      </button>

                      {/* Emoji button */}
                      <div style={{ position: "relative", display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <button
                          type="button"
                          className="chat-composer-icon-btn"
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                          title="Add Emoji"
                          disabled={isUploading}
                        >
                          <Smile size={18} />
                        </button>

                        {showEmojiPicker && (
                          <div ref={composerEmojiRef} style={{ position: "absolute", bottom: "50px", left: "0", zIndex: 100 }}>
                            <EmojiPicker onEmojiClick={onEmojiClick} />
                          </div>
                        )}
                      </div>

                      <input
                        type="text"
                        placeholder={isUploading ? "Uploading file..." : "Type a message..."}
                        value={inputText}
                        onChange={handleInputChange}
                        onPaste={handleInputPaste}
                        className="chat-composer-input flex-1 min-w-0"
                        disabled={isUploading}
                      />

                      {/* Send button */}
                      <button
                        type="submit"
                        disabled={!inputText.trim() || isUploading}
                        className={`chat-composer-send-btn flex-shrink-0 ${inputText.trim() ? "active" : ""}`}
                      >
                        <Send size={18} />
                      </button>
                    </form>
                  </div>
                ) : (
                  /* Blocked message composer warning overlay */
                  <div className="chat-blocked-composer-overlay animate__animated animate__fadeIn">
                    <span>
                      Messaging privilege has been revoked by the administrator.
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Empty active state viewport placeholder */
            <div className="chat-conversation-panel hidden md:flex">
              <div className="chat-empty-state">
                <div className="chat-empty-icon-wrapper">
                  <MessageSquare size={36} />
                </div>
                <h3 className="chat-empty-title">Enterprise Messaging</h3>
                <p className="chat-empty-subtitle">
                  Secure, real-time communications channel. Select a conversation from the sidebar to begin.
                </p>
              </div>
            </div>
          )}
        </div>
        {/* Image Lightbox Modal */}
        {lightboxImage && (
          <div
            onClick={() => setLightboxImage(null)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              zIndex: 99999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "16px"
            }}
          >
            <div style={{ position: "relative" }}>
              <img
                src={lightboxImage}
                alt="Enlarged Chat Attachment"
                style={{
                  maxWidth: "90vw",
                  maxHeight: "85vh",
                  borderRadius: "8px",
                  objectFit: "contain",
                  boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)"
                }}
              />
              <button
                onClick={() => setLightboxImage(null)}
                style={{
                  position: "absolute",
                  top: "-45px",
                  right: 0,
                  backgroundColor: "rgba(255,255,255,0.15)",
                  border: "none",
                  color: "white",
                  padding: "8px",
                  borderRadius: "50%",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <X size={20} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadImage(lightboxImage);
                }}
                style={{
                  position: "absolute",
                  top: "-45px",
                  right: "45px",
                  backgroundColor: "rgba(255,255,255,0.15)",
                  border: "none",
                  color: "white",
                  padding: "8px",
                  borderRadius: "50%",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Download size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Message Reaction Emoji Overlay */}
        {activeReactionMessageId && (
          <div
            onClick={() => setActiveReactionMessageId(null)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(15, 23, 42, 0.25)",
              backdropFilter: "blur(2px)",
              zIndex: 99999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "relative",
                animation: "zoomIn 0.15s ease-out"
              }}
            >
              <button
                onClick={() => setActiveReactionMessageId(null)}
                style={{ position: "absolute", top: "-10px", right: "-10px", background: "white", border: "1px solid #ccc", borderRadius: "50%", cursor: "pointer", zIndex: 10001, width: "24px", height: "24px", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <X size={14} />
              </button>
              <EmojiPicker onEmojiClick={handleMessageReaction} height={320} width={280} />
            </div>
          </div>
        )}

        {/* Forward Message Modal */}
        {showForwardModal && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.4)",
            backdropFilter: "blur(4px)",
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px"
          }}>
            <div style={{
              backgroundColor: "white",
              width: "100%",
              maxWidth: "420px",
              borderRadius: "16px",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
              display: "flex",
              flexDirection: "column",
              maxHeight: "80vh",
              overflow: "hidden"
            }}>
              {/* Header */}
              <div style={{
                padding: "16px 20px",
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}>
                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>Forward Message</h3>
                <button
                  onClick={() => {
                    setShowForwardModal(false);
                    setMessageToForward(null);
                    setSelectedForwardChats([]);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#64748b",
                    padding: "4px"
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Search */}
              <div style={{ padding: "12px 20px" }}>
                <div style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center"
                }}>
                  <Search size={16} style={{ position: "absolute", left: "12px", color: "#94a3b8" }} />
                  <input
                    type="text"
                    placeholder="Search chats..."
                    value={forwardSearchQuery}
                    onChange={(e) => setForwardSearchQuery(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px 12px 8px 36px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "8px",
                      fontSize: "14px",
                      outline: "none"
                    }}
                  />
                </div>
              </div>

              {/* Chat list */}
              <div style={{
                flex: 1,
                overflowY: "auto",
                padding: "0 20px 12px 20px"
              }}>
                {conversations
                  .filter((c) => {
                    if (c.isGroup) {
                      return c.name.toLowerCase().includes(forwardSearchQuery.toLowerCase());
                    } else {
                      const recipient = getRecipientInfo(c);
                      return recipient?.Name?.toLowerCase().includes(forwardSearchQuery.toLowerCase());
                    }
                  })
                  .map((c) => {
                    const recipient = getRecipientInfo(c);
                    const chatName = c.isGroup ? c.name : (recipient?.Name || "Chat");
                    const chatImage = c.isGroup ? c.groupImage : (recipient?.ProfilePhoto || defaultProfile);
                    const isSelected = selectedForwardChats.includes(c._id);

                    return (
                      <div
                        key={c._id}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedForwardChats(prev => prev.filter(id => id !== c._id));
                          } else {
                            setSelectedForwardChats(prev => [...prev, c._id]);
                          }
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "10px 12px",
                          borderRadius: "10px",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                          backgroundColor: isSelected ? "#f1f5f9" : "transparent",
                          marginBottom: "4px",
                          justifyContent: "space-between"
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <img
                            src={chatImage}
                            alt={chatName}
                            style={{ width: "36px", height: "36px", borderRadius: "50%", objectFit: "cover" }}
                            onError={(e) => { e.target.src = defaultProfile; }}
                          />
                          <span style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>{chatName}</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          style={{
                            width: "18px",
                            height: "18px",
                            accentColor: "#1e3a8a",
                            cursor: "pointer"
                          }}
                        />
                      </div>
                    );
                  })
                }
              </div>

              {/* Action Buttons */}
              <div style={{
                padding: "16px 20px",
                borderTop: "1px solid #f1f5f9",
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end"
              }}>
                <button
                  onClick={() => {
                    setShowForwardModal(false);
                    setMessageToForward(null);
                    setSelectedForwardChats([]);
                  }}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    background: "white",
                    color: "#64748b",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "pointer"
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleForwardMessage}
                  disabled={selectedForwardChats.length === 0}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "8px",
                    border: "none",
                    background: selectedForwardChats.length === 0 ? "#94a3b8" : "#1e3a8a",
                    color: "white",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: selectedForwardChats.length === 0 ? "not-allowed" : "pointer"
                  }}
                >
                  Forward {selectedForwardChats.length > 0 && `(${selectedForwardChats.length})`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* File/Image Upload and Clipboard Paste Confirmation Modal */}
        {isConfirmingSend && pendingSendFile && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(15, 23, 42, 0.6)",
              backdropFilter: "blur(4px)",
              zIndex: 99999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "16px"
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "16px",
                width: "100%",
                maxWidth: "420px",
                boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                border: "1px solid #f1f5f9",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                maxHeight: "90vh"
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid #f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "#f8fafc"
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                  <span style={{ fontSize: "14px", fontWeight: 800, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {pendingSendFiles && pendingSendFiles.length > 1
                      ? `${pendingSendFiles.length} Files Selected`
                      : (pendingSendFile.type.startsWith("image/") ? "Send Image" : "Send File")}
                  </span>
                  <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: "2px" }}>
                    {pendingSendFiles && pendingSendFiles.length > 1
                      ? `${pendingSendFiles.length} Files • ${formatFileSize(pendingTotalFileSize)} Total`
                      : `${pendingSendFile.name} • ${formatFileSize(pendingSendFile.size)}`}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsConfirmingSend(false);
                    setPendingSendFiles([]);
                    setPendingSendFile(null);
                    setFilePreviewUrl("");
                    setPendingCaption("");
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#94a3b8",
                    padding: "4px"
                  }}
                >
                  <X size={18} />
                </button>
              </div>

              {/* Preview Body */}
              <div
                style={{
                  padding: "20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f1f5f9",
                  flex: 1,
                  minHeight: "220px",
                  maxHeight: "360px",
                  overflow: "hidden"
                }}
              >
                {filePreviewUrl ? (
                  <img
                    src={filePreviewUrl}
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)"
                    }}
                  />
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", color: "#64748b" }}>
                    <div style={{ width: "64px", height: "64px", borderRadius: "16px", backgroundColor: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", color: "#475569" }}>
                      <FileText size={32} />
                    </div>
                    {pendingSendFiles && pendingSendFiles.length > 1 ? (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                        <span style={{ fontSize: "14px", fontWeight: 800, color: "#1e293b" }}>
                          {pendingSendFiles.length} Files Attached
                        </span>
                        <span style={{ fontSize: "12px", fontWeight: 700, color: "#2563eb" }}>
                          {formatFileSize(pendingTotalFileSize)} Total Size
                        </span>
                      </div>
                    ) : (
                      <span style={{ fontSize: "12px", fontWeight: 700, textAlign: "center", maxWidth: "240px", wordBreak: "break-all" }}>
                        {pendingSendFile.name}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Footer with Caption Input and Actions */}
              <div style={{ padding: "16px 20px", backgroundColor: "white", borderTop: "1px solid #f1f5f9" }}>
                {/* Caption Input */}
                <div className="chat-caption-input-wrapper">
                  <input
                    type="text"
                    placeholder="Add a caption..."
                    value={pendingCaption}
                    onChange={(e) => setPendingCaption(e.target.value)}
                    className="chat-caption-input"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        confirmSendFile(e);
                      }
                    }}
                    autoFocus
                  />
                </div>

                {/* Action Buttons */}
                <div style={{ display: "flex", justifyContent: "end", gap: "12px" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setIsConfirmingSend(false);
                      setPendingSendFile(null);
                      setFilePreviewUrl("");
                      setPendingCaption("");
                    }}
                    style={{
                      padding: "10px 20px",
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      background: "white",
                      color: "#64748b",
                      fontSize: "12px",
                      fontWeight: 700,
                      cursor: "pointer"
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmSendFile}
                    style={{
                      padding: "10px 24px",
                      borderRadius: "12px",
                      border: "none",
                      background: "#1e3a8a",
                      color: "white",
                      fontSize: "12px",
                      fontWeight: 700,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px"
                    }}
                  >
                    <Send size={14} /> Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Group Members List Modal */}
        {showMembersModal && activeConversation && activeConversation.isGroup && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(15, 23, 42, 0.4)",
              backdropFilter: "blur(2px)",
              zIndex: 99999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "16px"
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "16px",
                width: "100%",
                maxWidth: "360px",
                boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                border: "1px solid #f1f5f9",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                maxHeight: "80vh"
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid #f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "#f8fafc"
                }}
              >
                <span style={{ fontSize: "14px", fontWeight: 800, color: "#1e293b" }}>
                  Group Members ({activeConversation.participants.length})
                </span>
                <button
                  type="button"
                  onClick={() => setShowMembersModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#94a3b8",
                    padding: "4px"
                  }}
                >
                  <X size={18} />
                </button>
              </div>

              {/* List */}
              <div style={{ padding: "12px 16px", overflowY: "auto", flex: 1, backgroundColor: "white" }}>
                {activeConversation.participants.map((member) => {
                  const isAdmin = activeConversation.groupAdmin === member._id;
                  return (
                    <div
                      key={member._id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "8px 0",
                        borderBottom: "1px solid #f8fafc"
                      }}
                    >
                      <img
                        src={member.ProfilePhoto || defaultProfile}
                        alt={member.Name}
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          backgroundColor: "#f1f5f9"
                        }}
                        onError={(e) => {
                          e.target.src = defaultProfile;
                        }}
                      />
                      <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                        <span style={{ fontSize: "12.5px", fontWeight: 700, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {member.Name} {member._id === currentUserId && " (You)"}
                        </span>
                        <span style={{ fontSize: "10.5px", color: "#64748b", fontWeight: 550, textTransform: "capitalize", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {member.Role || "Staff"}
                        </span>
                      </div>
                      {isAdmin && (
                        <span style={{ fontSize: "9px", fontWeight: 700, color: "#1e3a8a", backgroundColor: "#eff6ff", padding: "2px 8px", borderRadius: "20px" }}>
                          Admin
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div style={{ padding: "12px 16px", backgroundColor: "#f8fafc", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "end" }}>
                <button
                  type="button"
                  onClick={() => setShowMembersModal(false)}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    background: "white",
                    color: "#64748b",
                    fontSize: "12px",
                    fontWeight: 700,
                    cursor: "pointer"
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatDrawer;
